import React, { useCallback, useEffect, useReducer } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import ReactModal from "react-modal";
import AsyncSelect from "react-select/async";
import moment from "moment-timezone";
import { formatTripDateRange } from "../../helpers/dates";
import { useDebouncedCallback } from "use-debounce";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";

import ai from "../../helpers/axios";
import { LoadingButton } from "../shared/loading-button";
import TextAreaInput from "../textarea-input";
import currencyFormatter from "../../helpers/currencyFormatter";
import { formatProspectOption, STRIPE_OBJECT_TYPE_LABELS } from "./helpers";
import getApiErrorMessage from "../../helpers/get-api-error-message";

const DEFAULT_OPTIONS_MAX_LIMIT = 50;

const formatTripOptionWithClient = ({ _id, tripTag, firstDepartureDate, lastArrivalDate, routing, clientName }) => ({
  value: _id,
  label: [
    tripTag,
    formatTripDateRange(firstDepartureDate, lastArrivalDate),
    clientName,
    routing ?? "-",
  ].filter(Boolean).join(" — "),
});

const buildInitialState = (row) => {
  const isStripe = !!row?.objectType;

  return {
    isSaving: false,
    prospectOption: null,
    tripOption: null,
    notes: (isStripe
      ? [
        { label: "Cardholder", value: row?.cardholderName },
        { label: "Reference", value: row?.description },
      ]
      : [
        { label: "Contact Name", value: row?.contactName },
        { label: "Reference", value: row?.reference },
      ]
    ).map(({ label, value }) => `${label}: ${value || "'-'"}`).join("; "),
  };
};

const reducer = (state, action) => {
  switch (action.type) {
    case "fieldChanged":
      return { ...state, [action.field]: action.value };
    case "reset":
      return buildInitialState(action.row);
    default:
      return state;
  }
};

const getProspects = async (token, params = {}) => {
  const { data } = await ai.auth(token).get("/api/prospects/search", { params });
  return data?.prospects ?? [];
};

const getProspectOptions = (prospects) =>
  prospects.flatMap(({ _id, firstAndLastName, lastName, firstName, emails, companyName, assigneeId, assigneeName }) => {
    if (!firstAndLastName && !lastName && !firstName) return [];
    return [formatProspectOption({
      _id,
      firstAndLastName,
      lastName,
      firstName,
      emails,
      companyName,
      assigneeId,
      assigneeName,
    })];
  });

const getTrips = async (token, params = {}) => {
  const { data } = await ai.auth(token).get("/api/trips/search", { params });
  return data?.data ?? [];
};

const LinkChargeToTripModal = ({ isOpen, row, onRequestClose }) => {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  const [state, dispatch] = useReducer(reducer, buildInitialState(row));

  useEffect(() => {
    if (isOpen) {
      dispatch({ type: "reset", row });
    }
  }, [isOpen, row?._id]);

  const getToken = useCallback(async () => {
    return getAccessTokenSilently().catch(() => loginWithRedirect());
  }, [getAccessTokenSilently, loginWithRedirect]);

  const handleClose = useCallback(() => {
    dispatch({ type: "reset", row });
    onRequestClose(false);
  }, [onRequestClose, row]);

  const loadProspects = useCallback(async (inputValue) => {
    const token = await getToken();
    const prospects = await getProspects(token, { q: inputValue, limit: DEFAULT_OPTIONS_MAX_LIMIT });
    return getProspectOptions(prospects);
  }, [getToken]);

  const debouncedLoadProspects = useDebouncedCallback(
    (inputValue, callback) => {
      loadProspects(inputValue).then(callback).catch(() => callback([]));
    },
    500,
  );

  const loadTrips = useCallback(async (inputValue) => {
    const token = await getToken();
    const trips = await getTrips(token, {
      q: inputValue,
      limit: DEFAULT_OPTIONS_MAX_LIMIT,
      prospectFilter: state.prospectOption?.value,
    });
    return trips.map(formatTripOptionWithClient);
  }, [getToken, state.prospectOption?.value]);

  const debouncedLoadTrips = useDebouncedCallback(
    (inputValue, callback) => {
      loadTrips(inputValue).then(callback).catch(() => callback([]));
    },
    500,
  );

  const handleSubmit = useCallback(async () => {
    if (!state.tripOption?.value) {
      return toast.error("Please select a trip.");
    }

    dispatch({ type: "fieldChanged", field: "isSaving", value: true });

    const token = await getToken();

    const provider = row?.objectType ? "stripe" : "xero";

    const response = await ai.auth(token)
      .post(`/api/${provider}/transactions/${row._id}/link-trip`, {
        tripId: state.tripOption.value,
        prospectId: state.prospectOption?.value || null,
        lineItemID: row?._lineItemID || null,
        notes: state.notes || "",
      })
      .catch((error) => {
        Sentry.captureException(error);
        toast.error(getApiErrorMessage(error, "Error linking charge to trip."));
      });

    if (response?.data) {
      toast.success("Charge linked to trip successfully.");
      dispatch({ type: "reset", row });
      await onRequestClose();
    }

    dispatch({ type: "fieldChanged", field: "isSaving", value: false });
  }, [state.tripOption, state.prospectOption, state.notes, row, getToken, onRequestClose]);

  return (
    <ReactModal
      isOpen={isOpen}
      shouldCloseOnEsc={false}
      shouldCloseOnOverlayClick={false}
      onRequestClose={handleClose}
      style={{
        overlay: { zIndex: 999999, backgroundColor: "rgb(32 53 68 / 50%)" },
        content: {
          width: "auto",
          border: "none",
          maxWidth: "700px",
          maxHeight: "85vh",
          minWidth: "calc(min(600px, 95vw))",
          borderRadius: "0",
          boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
        },
      }}
      appElement={document.getElementById("app")}
    >
      <div className="modal-header">
        <h4>Link Charge to a Trip</h4>
      </div>

      <div className="modal-body width-100 modal-body-with-scroll">
        {row?.objectType ? (
          <div className="w-100 d-flex gap-20 mb-15">
            <div className="flex-half">
              <div><b>Date:</b> {row?.chargedAt ? moment(row.chargedAt).format("M/D/YY") : "-"}</div>
              <div><b>Card Holder:</b> {row?.cardholderName || "-"}</div>
              <div><b>Type:</b> {STRIPE_OBJECT_TYPE_LABELS[row?.objectType] || "-"}</div>
            </div>
            <div className="flex-half">
              <div><b>Charged:</b> {currencyFormatter.format(row?.amount || 0)}</div>
              <div><b>Net to Evo:</b> {currencyFormatter.format(row?.netAmount || 0)}</div>
              <div><b>Stripe Fee:</b> {currencyFormatter.format(row?.feeAmount || 0)}</div>
            </div>
          </div>
        ) : (
          <div className="w-100 d-flex gap-20 mb-15">
            <div className="flex-half">
              <div><b>Date:</b> {row?.transactionDate ? moment(row.transactionDate).format("M/D/YY") : "-"}</div>
              <div><b>Contact Name:</b> {row?.contactName || "-"}</div>
              <div><b>Reference:</b> {row?.reference || "-"}</div>
            </div>
            <div className="flex-half">
              <div><b>Amount:</b> {currencyFormatter.format(row?.amount || 0)}</div>
            </div>
          </div>
        )}

        <form className="w-100 base-form mt-10">
          <div className="form-group">
            <label className="base-label">Filter by Prospect</label>
            <AsyncSelect
              classNamePrefix="select"
              loadOptions={debouncedLoadProspects}
              placeholder="Search/Select Prospect"
              onChange={(option) => {
                dispatch({ type: "fieldChanged", field: "prospectOption", value: option });
                dispatch({ type: "fieldChanged", field: "tripOption", value: null });
              }}
              value={state.prospectOption}
              isClearable
              menuPosition="fixed"
            />
          </div>

          <div className="form-group">
            <label className="base-label">
              Filter by Trip <span className="required">*</span>
            </label>
            <AsyncSelect
              classNamePrefix="select"
              loadOptions={debouncedLoadTrips}
              placeholder="Search/Select Trip"
              onChange={(option) => {
                dispatch({ type: "fieldChanged", field: "tripOption", value: option });
              }}
              value={state.tripOption}
              isClearable
              menuPosition="fixed"
            />
          </div>

          <TextAreaInput
            label="Notes"
            name="notes"
            containerClasses="mb-0"
            value={state.notes}
            handleChange={(e) => {
              dispatch({ type: "fieldChanged", field: "notes", value: e.target.value });
            }}
          />
        </form>
      </div>

      <div className="modal-footer no-border w-100 d-flex gap-10">
        <LoadingButton
          className="base-button evo-blue flex-half"
          onClick={handleSubmit}
          isLoading={state.isSaving}
          disabled={!state.tripOption?.value}
        >
          Submit
        </LoadingButton>

        <button
          className="base-button btn-danger flex-half"
          onClick={handleClose}
          disabled={state.isSaving}
        >
          Cancel
        </button>
      </div>
    </ReactModal>
  );
};

export default LinkChargeToTripModal;
